// The home's answers for the heat loss (the Full widget's Property page, for
// the installer): pre-filled from the EPC, editable. Values the EPC gave are
// marked, so the installer can see what they changed. The survey engine
// validates them (110-public-api full_input.py) and applies them only through
// RdSAP's published rules (docs/03 §8).
import type { FullProperty } from "@widget";
import { AGE_BANDS, FORMS, FUELS, HEATING_SYSTEMS, HOT_WATER, KINDS } from "./format";

type Tri = boolean | null;

function TriSelect({ id, value, onChange, naLabel }: { id: string; value: Tri; onChange: (v: Tri) => void; naLabel?: string }) {
  return (
    <select id={id} className="input" value={value === null ? "na" : value ? "yes" : "no"} onChange={(e) => onChange(e.target.value === "na" ? null : e.target.value === "yes")}>
      <option value="yes">Yes</option>
      <option value="no">No</option>
      {naLabel && <option value="na">{naLabel}</option>}
    </select>
  );
}

export function PropertyForm({ value, epc, onChange, disabled }: { value: FullProperty; epc?: Partial<FullProperty> | null; onChange: (p: FullProperty) => void; disabled?: boolean }) {
  const set = <K extends keyof FullProperty>(k: K, v: FullProperty[K]) => onChange({ ...value, [k]: v });
  // "from EPC" / "changed" marker beside each label
  const mark = (k: keyof FullProperty) => {
    if (!epc || epc[k] === undefined || epc[k] === null) return null;
    return epc[k] === value[k] ? <span className="q-epc-tag">EPC</span> : <span className="q-epc-tag is-changed">changed</span>;
  };
  const num = (k: "storeys" | "floorArea" | "bedrooms" | "bathrooms" | "radiators", lo: number, hi: number, unit?: string) => (
    <div className="ed-field">
      <label className="ed-label" htmlFor={`pf-${k}`}>
        {LABELS[k]} {mark(k)}
      </label>
      <div className="q-unit-input">
        <input id={`pf-${k}`} className="input" type="number" min={lo} max={hi} value={value[k]} disabled={disabled} onChange={(e) => set(k, Math.max(lo, Math.min(hi, Math.round(Number(e.target.value) || lo))))} />
        {unit && <span>{unit}</span>}
      </div>
    </div>
  );
  const pick = <K extends "kind" | "builtForm" | "ageBand" | "fuel" | "heatingSystem" | "hotWater">(k: K, opts: [string, string][]) => (
    <div className="ed-field">
      <label className="ed-label" htmlFor={`pf-${k}`}>
        {LABELS[k]} {mark(k)}
      </label>
      <select id={`pf-${k}`} className="input" value={value[k]} disabled={disabled} onChange={(e) => set(k, e.target.value as FullProperty[K])}>
        {opts.map(([v, l]) => (
          <option key={v} value={v}>
            {k === "ageBand" ? `${v}: ${l}` : l}
          </option>
        ))}
      </select>
    </div>
  );
  const flat = value.kind === "flat" || value.kind === "maisonette";
  return (
    <div className="q-prop-grid">
      {pick("kind", KINDS)}
      {!flat && pick("builtForm", FORMS)}
      {pick("ageBand", AGE_BANDS)}
      {num("floorArea", 15, 1000, "m²")}
      {num("storeys", 1, 5)}
      <div className="ed-field">
        <label className="ed-label" htmlFor="pf-roomInRoof">
          Room in the roof {mark("roomInRoof")}
        </label>
        <TriSelect id="pf-roomInRoof" value={value.roomInRoof} onChange={(v) => set("roomInRoof", Boolean(v))} />
      </div>
      {num("bedrooms", 1, 10)}
      {num("bathrooms", 1, 6)}
      {num("radiators", 0, 40)}
      {pick("fuel", FUELS)}
      {pick("heatingSystem", HEATING_SYSTEMS)}
      {pick("hotWater", HOT_WATER)}
      <div className="ed-field">
        <label className="ed-label" htmlFor="pf-wallsInsulated">
          Walls insulated {mark("wallsInsulated")}
        </label>
        <TriSelect id="pf-wallsInsulated" value={value.wallsInsulated} onChange={(v) => set("wallsInsulated", Boolean(v))} />
      </div>
      <div className="ed-field">
        <label className="ed-label" htmlFor="pf-roofInsulated">
          Loft / roof insulated {mark("roofInsulated")}
        </label>
        <TriSelect id="pf-roofInsulated" value={value.roofInsulated} onChange={(v) => set("roofInsulated", v)} naLabel="Another home above" />
      </div>
      <div className="ed-field">
        <label className="ed-label" htmlFor="pf-floorInsulated">
          Floor insulated {mark("floorInsulated")}
        </label>
        <TriSelect id="pf-floorInsulated" value={value.floorInsulated} onChange={(v) => set("floorInsulated", v)} naLabel="Another home below" />
      </div>
      <div className="ed-field">
        <label className="ed-label" htmlFor="pf-mainsGas">
          Mains gas connection {mark("mainsGas")}
        </label>
        <TriSelect id="pf-mainsGas" value={value.mainsGas} onChange={(v) => set("mainsGas", v)} naLabel="Not known" />
      </div>
    </div>
  );
}

const LABELS: Record<keyof FullProperty, string> = {
  kind: "Property type",
  builtForm: "Built form",
  ageBand: "Age band",
  storeys: "Floors",
  roomInRoof: "Room in the roof",
  floorArea: "Floor area",
  fuel: "Heating fuel today",
  heatingSystem: "Heating system",
  hotWater: "Hot water",
  bedrooms: "Bedrooms",
  bathrooms: "Bathrooms",
  radiators: "Radiators",
  wallsInsulated: "Walls insulated",
  roofInsulated: "Loft / roof insulated",
  floorInsulated: "Floor insulated",
  mainsGas: "Mains gas",
};
